import {
  AppShell,
  Avatar,
  Badge,
  Box,
  Burger,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconLogout } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigation } from 'react-router';

import { useAdminQueues } from '@/api/queues';
import type { AdminQueues } from '@/api/types';
import { useAuth } from '@/auth/context';
import { Brand } from '@/components/Brand';
import { LanguageSwitch } from '@/components/LanguageSwitch';
import { paths } from '@/utils/paths';

import classes from './AdminShell.module.css';
import { formatBadgeCount, isNavItemActive, NAV_SECTIONS, type NavItem } from './navigation';

const HEADER_HEIGHT = 52;
const NAVBAR_WIDTH = 272;

function QueueBadges({ item, queues }: { item: NavItem; queues: AdminQueues | undefined }) {
  const { t } = useTranslation('nav');
  if (!queues || !item.badges) return null;
  const visible = item.badges.filter((badge) => (queues[badge.counter] ?? 0) > 0);
  if (visible.length === 0) return null;
  return (
    <Group gap={4} wrap="nowrap">
      {visible.map((badge) => (
        <Tooltip key={badge.key} label={t(`badges.${badge.key}`)} position="right">
          {/* The tooltip is not part of the link's name: the badge carries the meaning itself. */}
          <Badge
            size="sm"
            radius="xl"
            variant={badge.variant}
            color={badge.color}
            miw={22}
            role="img"
            aria-label={`${t(`badges.${badge.key}`)}: ${queues[badge.counter]}`}
          >
            {formatBadgeCount(queues[badge.counter])}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
}

function UserMenu() {
  const { t } = useTranslation(['nav', 'common']);
  const { me, logout } = useAuth();
  if (!me) return null;
  const displayName = me.name || me.email || t('common:values.userFallback', { id: me.id });
  return (
    <Menu position="bottom-end" width={260} withArrow>
      <Menu.Target>
        <UnstyledButton className={classes.userButton} aria-label={t('account.menu')}>
          <Group gap={8} wrap="nowrap">
            <Avatar name={displayName} color="initials" size={30} radius="xl" />
            <Box visibleFrom="sm" maw={200} style={{ lineHeight: 1.2 }}>
              <Text size="sm" fw={500} truncate>
                {displayName}
              </Text>
              <Text size="xs" c="dimmed">
                {me.is_superuser ? t('account.superuser') : t('account.staff')}
              </Text>
            </Box>
            <IconChevronDown size={14} stroke={1.75} />
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {me.email && <Menu.Label>{me.email}</Menu.Label>}
        <Menu.Item color="red" leftSection={<IconLogout size={16} stroke={1.75} />} onClick={logout}>
          {t('common:actions.logout')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/** Authenticated layout: header (brand, language, account) + sidebar with queue badges. */
export function AdminShell() {
  const { t } = useTranslation('nav');
  const [navOpened, { toggle: toggleNav, close: closeNav }] = useDisclosure(false);
  const { pathname } = useLocation();
  const navigation = useNavigation();
  const { data: queues } = useAdminQueues();

  return (
    <AppShell
      header={{ height: HEADER_HEIGHT }}
      navbar={{ width: NAVBAR_WIDTH, breakpoint: 'md', collapsed: { mobile: !navOpened } }}
      padding="lg"
    >
      <AppShell.Header className={classes.header}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={navOpened}
              onClick={toggleNav}
              hiddenFrom="md"
              size="sm"
              aria-label={t('toggleNavigation')}
            />
            <Brand to={paths.dashboard} />
          </Group>
          <Group gap="md" wrap="nowrap">
            <LanguageSwitch />
            <UserMenu />
          </Group>
        </Group>
        {navigation.state === 'loading' && <div className={classes.progress} aria-hidden="true" />}
      </AppShell.Header>

      <AppShell.Navbar className={classes.navbar} p="xs">
        <AppShell.Section grow component={ScrollArea} scrollbars="y">
          <nav>
            {NAV_SECTIONS.map((section) => (
              <Box key={section.key ?? 'top'} component="section">
                {section.key && <div className={classes.sectionLabel}>{t(`sections.${section.key}`)}</div>}
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.key}
                      component={Link}
                      to={item.to}
                      onClick={closeNav}
                      className={classes.navLink}
                      active={isNavItemActive(pathname, item)}
                      label={t(`items.${item.key}`)}
                      description={item.hint ? t(`items.${item.hint}`) : undefined}
                      leftSection={<Icon size={18} stroke={1.6} />}
                      rightSection={<QueueBadges item={item} queues={queues} />}
                    />
                  );
                })}
              </Box>
            ))}
          </nav>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main className={classes.main}>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
